import type { RouterHistory } from "@tanstack/history";
import {
    type AnyContext,
    type AnyRoute,
    type AnyRouteMatch,
    type AnyRouter,
    BaseRootRoute,
    BaseRoute,
    type Register,
    type ResolveFullPath,
    type ResolveId,
    type ResolveParams,
    type RootRouteOptions,
    type RouteConstraints,
    type RouteOptions,
    type RouterConstructorOptions,
    RouterCore,
    type TrailingSlashOption,
    getLocationChangeInfo,
    trimPathRight,
} from "@tanstack/router-core";
import type { TemplateResult } from "lit";
import type { RouteNode } from "../route-tree";
import { getSignalStoreConfig } from "./stores";

export interface RouteRenderArgs {
    match: AnyRouteMatch;
    router: AnyRouter;
}

export interface RouteErrorRenderArgs extends RouteRenderArgs {
    error: unknown;
}

export type RouteComponent = (args: RouteRenderArgs) => TemplateResult;
export type RouteErrorComponent = (args: RouteErrorRenderArgs) => TemplateResult;

declare module "@tanstack/router-core" {
    interface UpdatableRouteOptionsExtensions {
        /** Renders the route's view; data comes from `match`, intents bubble up as events (see intents.ts). */
        component?: RouteComponent;
        /** Shown instead of `component` while a slow first load runs (after `pendingMs`). */
        pendingComponent?: RouteComponent;
        errorComponent?: RouteErrorComponent;
    }

    interface RouterOptionsExtensions {
        defaultPendingComponent?: RouteComponent;
        defaultErrorComponent?: RouteErrorComponent;
    }

    interface StaticDataRouteOption {
        /** Tab title (standalone only; the host owns it when embedded); a function for titles from params. */
        title?: string | ((args: { match: AnyRouteMatch; context: any }) => string | undefined);
        helpPage?: string;
        /** Menu metadata, shared with the legacy route tree while both exist. */
        menu?: RouteNode;
    }
}

/**
 * For `route.lazy()`: router-core merges the resolved options into the route;
 * for Lit the import itself is the point (it defines the view's element).
 */
export function lazyElement(load: () => Promise<unknown>) {
    return () => load().then(() => ({ options: {} }) as never);
}

/** The Lit adapter's router: router-core with signal-backed stores. */
export class LitRouter<
    TRouteTree extends AnyRoute,
    TTrailingSlashOption extends TrailingSlashOption = "never",
    TDefaultStructuralSharingOption extends boolean = false,
    TRouterHistory extends RouterHistory = RouterHistory,
    TDehydrated extends Record<string, any> = Record<string, any>,
> extends RouterCore<TRouteTree, TTrailingSlashOption, TDefaultStructuralSharingOption, TRouterHistory, TDehydrated> {
    constructor(
        options: RouterConstructorOptions<
            TRouteTree,
            TTrailingSlashOption,
            TDefaultStructuralSharingOption,
            TRouterHistory,
            TDehydrated
        >
    ) {
        super(options, getSignalStoreConfig);
    }
}

export function createRootRouteWithContext<TRouterContext extends object>() {
    return <
        TRegister = Register,
        TRouteContextFn = AnyContext,
        TBeforeLoadFn = AnyContext,
        TSearchValidator = undefined,
        // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- must match router-core\'s own default
        TLoaderDeps extends Record<string, any> = {},
        TLoaderFn = undefined,
    >(
        options?: RootRouteOptions<
            TRegister,
            TSearchValidator,
            TRouterContext,
            TRouteContextFn,
            TBeforeLoadFn,
            TLoaderDeps,
            TLoaderFn
        >
    ) =>
        new BaseRootRoute<
            TRegister,
            TSearchValidator,
            TRouterContext,
            TRouteContextFn,
            TBeforeLoadFn,
            TLoaderDeps,
            TLoaderFn
        >(options);
}

export function createRoute<
    TRegister = unknown,
    TParentRoute extends RouteConstraints["TParentRoute"] = AnyRoute,
    TPath extends RouteConstraints["TPath"] = "/",
    TFullPath extends RouteConstraints["TFullPath"] = ResolveFullPath<TParentRoute, TPath>,
    TCustomId extends RouteConstraints["TCustomId"] = string,
    TId extends RouteConstraints["TId"] = ResolveId<TParentRoute, TCustomId, TPath>,
    TSearchValidator = undefined,
    TParams = ResolveParams<TPath>,
    TRouteContextFn = AnyContext,
    TBeforeLoadFn = AnyContext,
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- must match router-core\'s own default
    TLoaderDeps extends Record<string, any> = {},
    TLoaderFn = undefined,
>(
    options: RouteOptions<
        TRegister,
        TParentRoute,
        TId,
        TCustomId,
        TFullPath,
        TPath,
        TSearchValidator,
        TParams,
        TLoaderDeps,
        TLoaderFn,
        AnyContext,
        TRouteContextFn,
        TBeforeLoadFn
    >
) {
    return new BaseRoute<
        TRegister,
        TParentRoute,
        TPath,
        TFullPath,
        TCustomId,
        TId,
        TSearchValidator,
        TParams,
        AnyContext,
        TRouteContextFn,
        TBeforeLoadFn,
        TLoaderDeps,
        TLoaderFn
    >(options);
}

/**
 * What the React/Solid adapters do in their `<Transitioner>`: follow the
 * history, canonicalize the initial URL, run the first load and emit
 * `onRendered`. Returns the teardown.
 */
export function startRouter(router: AnyRouter) {
    const unsubscribeHistory = router.history.subscribe(router.load);
    const unsubscribeResolved = router.subscribe("onResolved", () => {
        // Views render on the next microtask once the stores settle.
        queueMicrotask(() => {
            const resolved = router.stores.resolvedLocation.get();
            if (resolved) {
                router.emit({ type: "onRendered", ...getLocationChangeInfo(resolved, resolved) });
            }
        });
    });

    const next = router.buildLocation({
        to: router.latestLocation.pathname,
        search: true,
        params: true,
        hash: true,
        state: true,
        _includeValidateSearch: true,
    });
    if (trimPathRight(router.latestLocation.publicHref) !== trimPathRight(next.publicHref)) {
        void router.commitLocation({ ...next, replace: true, ignoreBlocker: true });
    } else {
        router.load().catch(console.error);
    }

    return () => {
        unsubscribeHistory();
        unsubscribeResolved();
    };
}
